import { createSlice } from '@reduxjs/toolkit'
import api from 'api/axios/foka'
import { createAxiosThunk } from 'utils/reduxUtil'
import { getThumbnailAssetData } from 'features/creative/add/asset/thumbnailSlice'
import { getMultiThumbnailAssetData } from 'features/creative/add/asset/multiThumbnailSlice'
import { getAdditionalTitleAssetData } from 'features/creative/add/asset/additionalTitleSlice'
import { getAdditionalLinkAssetData } from 'features/creative/add/asset/additionalLinkSlice'
import { getSubjectAssetData } from 'features/creative/add/asset/subjectSlice'
import { getPriceTableAssetData } from 'features/creative/add/asset/priceTableSlice'
import { getCalculateAssetData } from 'features/creative/add/asset/calculateSlice'
import { getTalkChannelAssetData } from 'features/creative/add/asset/talkChannelSlice'
import { getPhoneNumberAssetData } from 'features/creative/add/asset/phoneNumberSlice'
import { getBasicCreativeData } from 'features/creative/add/basic/basicCreativeSlice'
import { isPresent } from 'utils'
import { checkLoanInsurance } from 'features/creative/creativeUtil'
import { BizChannelTypes } from '../../../Codes'
import { getThumbnailRecommendAssetData } from '../asset/thumbnailRecommendSlice'
import { getThumbnailCustomAssetData } from '../asset/thumbnailCustomSlice'

const initialState = {
  isLoading: false,
  channel: null,
  creativeName: '',
  isCreativesLoading: true,
  activeAssets: {
    thumbnail: false,
    multiThumbnail: false,
    additionalTitle: false,
    additionalLink: false,
    subject: false,
    priceTable: false,
    calculate: false,
    phoneNumber: false,
    talkChannel: false,
  },
}

const generateCreative = getState => {
  const {
    basicCreative,
    buildCreative: { activeAssets, channel, creativeName },
    assets: {
      thumbnail,
      multiThumbnail,
      additionalTitle,
      additionalLink,
      subject,
      priceTable,
      calculate,
      talkChannel,
      phoneNumber,
      thumbnailRecommend,
      thumbnailCustom,
    },
  } = getState().creative
  const { bizChannelType } = channel
  const assets =
    bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL
      ? [
          activeAssets.thumbnail && getThumbnailAssetData(thumbnail),
          activeAssets.multiThumbnail &&
            getMultiThumbnailAssetData(multiThumbnail),
          activeAssets.additionalTitle &&
            getAdditionalTitleAssetData(additionalTitle),
          activeAssets.additionalLink &&
            getAdditionalLinkAssetData(additionalLink),
          activeAssets.subject && getSubjectAssetData(subject),
          activeAssets.priceTable && getPriceTableAssetData(priceTable),
          activeAssets.calculate && getCalculateAssetData(calculate),
          activeAssets.talkChannel && getTalkChannelAssetData(talkChannel),
          activeAssets.phoneNumber && getPhoneNumberAssetData(phoneNumber),
        ].filter(Boolean)
      : [
          getThumbnailRecommendAssetData(
            thumbnailRecommend,
            basicCreative.licenseNumber,
            basicCreative.license,
            basicCreative.bothLegalNoticeChecked
          ),
          getThumbnailCustomAssetData(thumbnailCustom),
        ]

  const dspImageIds = []
  if (activeAssets.thumbnail && isPresent(thumbnail.id)) {
    dspImageIds.push(thumbnail.id)
  }
  if (activeAssets.multiThumbnail) {
    multiThumbnail.images.forEach(({ id }) => {
      if (isPresent(id)) {
        dspImageIds.push(id)
      }
    })
  }
  if (bizChannelType === BizChannelTypes.TALK_CHANNEL) {
    if (thumbnailRecommend?.thumbnail?.id) {
      dspImageIds.push(thumbnailRecommend.thumbnail.id)
    }
    if (thumbnailCustom?.thumbnail?.id) {
      dspImageIds.push(thumbnailCustom.thumbnail.id)
    }
  }

  return {
    bizChannelType,
    name: creativeName,
    bizChannelId: channel.id,
    ...getBasicCreativeData(basicCreative, bizChannelType),
    ...(bizChannelType === BizChannelTypes.WEBSITE_BIZ_CHANNEL
      ? { assets }
      : { templates: assets }),
    dspImageIds,
  }
}

export const fetchCreativeGenerable = createAxiosThunk(
  'buildCreative/fetchCreativeGenerable',
  (noArgs, { getState }) => {
    const { channel } = getState().creative.buildCreative
    return api.creative.getCreativeIsAddable(channel.id)
  }
)

export const addImageLibrary = createAxiosThunk(
  'buildCreative/addImageLibrary',
  images =>
    api.image.postDspImages({
      commands: images.map(({ url, width, height, name, size }) => ({
        url,
        width,
        height,
        name,
        size,
      })),
    })
)

export const addCreative = createAxiosThunk(
  'buildCreative/addCreative',
  (noArgs, { getState }) => {
    const createBasicCreativeCommand = generateCreative(getState)
    return api.creative.addCreative(createBasicCreativeCommand)
  }
)

export const saveAsCreative = createAxiosThunk(
  'buildCreative/saveAsCreative',
  (adGroupId, { getState }) => {
    const createBasicCreativeCommand = generateCreative(getState)
    return api.creative.saveAsCreative({
      ...createBasicCreativeCommand,
      adGroupId,
    })
  }
)

const buildCreativeSlice = createSlice({
  name: 'buildCreative',
  initialState,
  reducers: {
    reset: () => initialState,
    resetActiveAssets: state => {
      state.activeAssets = initialState.activeAssets
    },
    setChannel: (state, { payload: channel }) => {
      state.channel = channel
      const isLoanInsurance = checkLoanInsurance(channel)
      if (!isLoanInsurance) {
        state.activeAssets.calculate = false
      }
    },
    setCreativeName: (state, { payload: creativeName }) => {
      state.creativeName = creativeName
    },
    setAssetChecked: (state, { payload: assetChecked }) => {
      Object.keys(state.activeAssets).forEach(key => {
        state.activeAssets[key] = assetChecked[key]
      })
    },
    inactiveAsset: (state, { payload }) => {
      state.activeAssets[payload] = false
    },
    inactiveAllAsset: state => {
      Object.keys(state.activeAssets).forEach(key => {
        state.activeAssets[key] = false
      })
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchCreativeGenerable.pending, state => {
      state.isLoading = true
    })

    builder.addCase(fetchCreativeGenerable.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(fetchCreativeGenerable.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(addImageLibrary.pending, state => {
      state.isLoading = true
    })

    builder.addCase(addImageLibrary.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(addImageLibrary.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(addCreative.pending, state => {
      state.isLoading = true
    })

    builder.addCase(addCreative.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(addCreative.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(saveAsCreative.pending, state => {
      state.isLoading = true
    })

    builder.addCase(saveAsCreative.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(saveAsCreative.rejected, state => {
      state.isLoading = false
    })
  },
})

export const {
  reset,
  resetActiveAssets,
  setChannel,
  setCreativeName,
  setAssetChecked,
  inactiveAsset,
  inactiveAllAsset,
} = buildCreativeSlice.actions

export default buildCreativeSlice.reducer
